import { useEffect, useRef, useState } from 'react';

import { cn } from '~/utils/cn';

import { Box } from '../Box/view';
import { Icon } from '../Icon/view';
import { Text } from '../Text/view';

export interface SelectOption<T = string> {
  label: string;
  value: T;
  description?: string;
}

interface PropsSelect<T = string> {
  options: SelectOption<T>[];
  value?: T;
  onChange?: (value: T) => void;
  placeholder?: string;
  label?: string;
  errorMessage?: string;
  disabled?: boolean;
  className?: string;
}

export default function Select<T extends string | number>({
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  label,
  errorMessage,
  disabled,
  className,
}: PropsSelect<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <Box className="flex flex-col gap-quark w-full" id={undefined}>
      {label && <Text className="text-bodyMD color-intense-07">{label}</Text>}
      <div ref={ref} className="relative w-full">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((p) => !p)}
          className={cn(
            'flex items-center justify-between gap-nano px-small h-[56px] w-full rounded-small bg-intense-02 border',
            errorMessage ? 'border-warning-04' : 'border-intense-03',
            disabled && 'opacity-60 cursor-not-allowed',
            className,
          )}
        >
          <Text
            className={cn(
              'text-bodyMD',
              selected ? 'color-complement-01' : 'color-intense-05',
            )}
          >
            {selected?.label ?? placeholder}
          </Text>
          <Icon name={open ? 'icon-chevron-up' : 'icon-chevron-down'} size={18} color="#606060" />
        </button>
        {open && (
          <div className="absolute left-0 right-0 top-[60px] z-30 max-h-[280px] overflow-auto rounded-small bg-intense-01 border border-intense-03 shadow-level2">
            {options.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => {
                  onChange?.(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full flex-col items-start px-small py-smaller text-left hover:bg-intense-02',
                  opt.value === value && 'bg-support-01',
                )}
              >
                <Text className="text-bodyMD color-complement-01">{opt.label}</Text>
                {opt.description && (
                  <Text className="text-captionMD color-intense-06">
                    {opt.description}
                  </Text>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      {errorMessage && (
        <Text className="text-captionMD color-warning-06">{errorMessage}</Text>
      )}
    </Box>
  );
}
